<template>
  <div>
    <div class="row">
      <div class="col-md-6">
        <h3>{{ $t('employee.tabs.lastDayPay') }}</h3>
        <ol>
          <li
            v-for="(bullet, i) in Array.isArray(GUIDANCE_BULLETS[partnership])
              ? GUIDANCE_BULLETS[partnership]
              : GUIDANCE_BULLETS['default']"
            :key="i"
          >
            {{ bullet }}
          </li>
          <div>
            <p class="success-line">
              {{ $t('employee.lastDayPay.routingNumberLabel') }}:
              {{ employee.properties.payment_sort_code || $t('employee.lastDayPay.noRoutingNumberFallback') }}
            </p>
            <p class="success-line">
              {{ $t('employee.lastDayPay.accountNumberLabel') }}:
              {{ employee.properties.payment_account_number || $t('employee.lastDayPay.noAccountNumberFallback') }}
            </p>
          </div>
        </ol>
      </div>

      <div class="col-md-6">
        <div class="last-day-pay__title">
          {{ $t('employee.lastDayPay.totalDebtLabel') }}
          <span>${{ isSettlementConfirmed ? toDecimalNotation(totalDebtDisplay) : toDecimalNotation(totalDebt) }}</span>
        </div>

        <div v-if="isSettlementConfirmed">
          <div class="last-day-pay__title">
            {{ $t('employee.lastDayPay.finalNetPayLabel') }}
            <span>${{ toDecimalNotation(finalNetPayDisplay) }}</span>
          </div>
          <p>{{ $t('employee.lastDayPay.finalNetPayHelpSettlement') }}</p>
        </div>
        <form v-else class="last-day-pay__form">
          <b-form-group class="last-day-pay__form-group">
            <label for="final-net-pay-input">{{ $t('employee.lastDayPay.finalNetPayLabel') }}</label>
            <b-input-group>
              <b-input-group-prepend>
                <div class="input-group-text">
                  <font-awesome-icon :icon="['fa', 'dollar-sign']" />
                </div>
              </b-input-group-prepend>

              <b-form-input
                id="final-net-pay-input"
                type="number"
                v-model="finalNetPayValue"
                :min="totalDebt"
                :max="maxFinalNetPayValue"
                step="0.01"
                debounce="500"
              ></b-form-input>
            </b-input-group>
          </b-form-group>
          <p>{{ $t('employee.lastDayPay.finalNetPayHelpSettlement') }}</p>
        </form>

        <div class="last-day-pay__title">
          {{ $t('employee.lastDayPay.remainingViaChequeLabel') }}
          <span
            >${{
              isSettlementConfirmed
                ? toDecimalNotation(remainingViaChequeDisplay)
                : toDecimalNotation(remainingViaCheque)
            }}
          </span>
        </div>
        <p>{{ $t('employee.lastDayPay.remainingViaChequeHelp') }}</p>

        <sme-alert v-if="isSettlementConfirmed" level="good" icon="check">
          <p class="success-line _heading">{{ $t('employee.lastDayPay.settlementCreatedTitle') }}</p>
          <p class="success-line">{{ $t('employee.lastDayPay.employeeAccountDisabled') }}</p>
          <p class="success-line">
            <i18n path="employee.lastDayPay.outstandingBalanceSettledBody" tag="span">
              <template #amount
                ><strong>${{ toDecimalNotation(totalDebtDisplay) }}</strong></template
              >
            </i18n>
          </p>
          <p class="success-line">
            <i18n path="employee.lastDayPay.finalNetPayEnteredBody" tag="span">
              <template #amount
                ><strong>${{ toDecimalNotation(finalNetPayDisplay) }}</strong></template
              >
            </i18n>
          </p>
          <p class="success-line">
            <i18n path="employee.lastDayPay.remainingViaChequeBody" tag="span">
              <template #amount
                ><strong>${{ toDecimalNotation(remainingViaChequeDisplay) }}</strong></template
              >
            </i18n>
          </p>
          <div v-if="isStatementAvailable" class="next-steps">
            <p class="success-line _marked">{{ $t('employee.lastDayPay.statementLabel') }}</p>
            <p class="success-line">{{ $t('employee.lastDayPay.statementEmailedBody') }}</p>
            <p class="success-line">
              <b-button variant="link" class="download-link" :disabled="isDownloading" @click="onDownloadStatement">
                <font-awesome-icon :icon="['far', 'file-pdf']" class="mr-1" />
                {{ $t('employee.lastDayPay.downloadStatementButton') }}
              </b-button>
            </p>
            <p v-if="downloadError" class="success-line text-danger">{{ downloadError }}</p>
          </div>
        </sme-alert>
        <sme-alert v-else-if="isError" level="danger" icon="times-circle">{{ errMessage }}</sme-alert>

        <div v-if="!isSettlementConfirmed" class="col-md-12 mt-5 d-flex send-action">
          <b-button
            @click="onSubmit"
            variant="primary"
            :disabled="+finalNetPayValue < +totalDebt || !+finalNetPayValue"
          >
            {{ $t('employee.lastDayPay.createFinalSettlementButton') }}
          </b-button>
        </div>
      </div>
    </div>
    <b-modal
      v-model="showModal"
      :ok-title="$t('employee.lastDayPay.createFinalSettlementButton')"
      ok-variant="primary"
      :title="$t('employee.lastDayPay.confirmSettlementModalTitle')"
      body-class="pt-0"
      size="lg"
      centered
      @cancel="onPopupClose"
      @ok="onSettlementConfirm"
    >
      <div class="summary__header">
        <font-awesome-icon :icon="['fad', 'file-invoice-dollar']" class="summary__icon" />
      </div>
      <AppLoading :loading="isRequestInProgress" />
      <div class="summary__subtitle">
        <div>
          {{ $t('employee.lastDayPay.disableAndEmailWarning') }}
        </div>
      </div>
      <div class="summary__row">
        <div>{{ $t('employee.lastDayPay.modalFinalNetPayLabel') }}</div>
        <div>{{ toDecimalNotation(finalNetPayValue) }}</div>
      </div>
      <div class="summary__row">
        <div>{{ $t('employee.lastDayPay.modalTotalDebtLabel') }}</div>
        <div>- {{ toDecimalNotation(totalDebt) }}</div>
      </div>
      <div class="summary__row _total">
        <div>{{ $t('employee.lastDayPay.modalRemainingViaChequeLabel') }}</div>
        <div>{{ toDecimalNotation(remainingViaCheque) }}</div>
      </div>
    </b-modal>
  </div>
</template>

<script setup>
import i18n from '@/i18n';

const STEP_1 = i18n.t('employee.lastDayPay.settlementGuidanceStep1');
const STEP_2 = i18n.t('employee.lastDayPay.settlementGuidanceStep2');
const STEP_3_STREAM = i18n.t('employee.lastDayPay.settlementGuidanceStep3Stream');
const STEP_4 = i18n.t('employee.lastDayPay.settlementGuidanceStep4');
const STEP_5_STREAM = i18n.t('employee.lastDayPay.settlementGuidanceStep5Stream');

const GUIDANCE_BULLETS = {
  lip: [
    STEP_1,
    STEP_2,
    i18n.t('employee.lastDayPay.settlementGuidanceStep3Lip'),
    STEP_4,
    i18n.t('employee.lastDayPay.settlementGuidanceStep5Lip'),
  ],
  wagestream: [STEP_1, STEP_2, STEP_3_STREAM, STEP_4, STEP_5_STREAM],
  default: [STEP_1, STEP_2, STEP_3_STREAM, STEP_4, STEP_5_STREAM],
};
</script>

<script>
import ApiClient from '@/ApiClient';
import { getPartnership } from '@/utils/partnership';
import AppLoading from '@/components/AppLoading.vue';
import SmeAlert from '@/components/atoms/SmeAlert.vue';
import { LAST_DAY_PAY_UPPER_BOUND } from '@/pages/employee-payments/constants';

export default {
  name: 'EmployeeLastDayPayV3',
  model: {
    prop: 'employee',
    event: 'input',
  },
  props: {
    employee: Object,
    balance: Object,
    currency: String,
  },
  components: { SmeAlert },
  data() {
    return {
      finalNetPayValue: +this.balance.total_debt / 100,
      showModal: false,
      isError: false,
      errMessage: null,
      isRequestInProgress: false,
      maxFinalNetPayValue: LAST_DAY_PAY_UPPER_BOUND,
      partnership: getPartnership(),
      settlementResponse: null,
      isDownloading: false,
      downloadError: null,
    };
  },
  methods: {
    async onSettlementConfirm() {
      this.isRequestInProgress = true;
      this.isError = false;
      this.errMessage = null;

      const payload = {
        employee_id: this.employee.employee_id,
        final_net_pay: Math.round(this.finalNetPayValue * 100),
      };

      try {
        const response = await ApiClient.postLastDayPaySettlement(payload);
        const data = response?.data || response;
        if (response?.error) {
          throw response.error;
        }
        this.settlementResponse = data;
        this.$emit('finalPayProcessed');
      } catch (err) {
        this.isError = true;
        this.errMessage = err?.message || this.$t('employee.lastDayPay.failedSettlementFallback');
      } finally {
        this.isRequestInProgress = false;
        this.showModal = false;
      }
    },
    onPopupClose() {
      this.showModal = false;
    },
    onSubmit() {
      this.showModal = true;
    },
    async onDownloadStatement() {
      this.isDownloading = true;
      this.downloadError = null;
      try {
        await ApiClient.downloadLastDayPaySettlementStatement(this.employee.employee_id);
      } catch (err) {
        this.downloadError = this.$t('employee.lastDayPay.downloadErrorFallback');
      } finally {
        this.isDownloading = false;
      }
    },
  },
  computed: {
    totalDebt() {
      return +this.balance.total_outstanding / 100;
    },
    remainingViaCheque() {
      const diff = +this.finalNetPayValue - +this.totalDebt;
      return diff > 0 ? diff : 0;
    },
    toDecimalNotation() {
      return value => (+value ? (+value).toFixed(2) : '0.00');
    },
    hasExistingFinalPay() {
      return Boolean(this.employee?.properties?.final_pay);
    },
    isSettlementConfirmed() {
      return Boolean(this.settlementResponse) || this.hasExistingFinalPay;
    },
    isStatementAvailable() {
      if (this.settlementResponse) {
        return Boolean(this.settlementResponse.statement_download_url);
      }
      return Boolean(this.employee?.properties?.final_settlement_pdf_key);
    },
    totalDebtDisplay() {
      if (this.settlementResponse) {
        return this.settlementResponse.total_debt_minor / 100;
      }
      return (this.employee?.properties?.final_total_debt || 0) / 100;
    },
    finalNetPayDisplay() {
      if (this.settlementResponse) {
        return this.settlementResponse.final_net_pay_minor / 100;
      }
      return (this.employee?.properties?.final_net_pay_amount || 0) / 100;
    },
    remainingViaChequeDisplay() {
      if (this.settlementResponse) {
        return this.settlementResponse.remaining_via_cheque_minor / 100;
      }
      const total = this.employee?.properties?.final_net_pay_amount || 0;
      const debt = this.employee?.properties?.final_total_debt || 0;
      const diff = (total - debt) / 100;
      return diff > 0 ? diff : 0;
    },
  },
  watch: {
    finalNetPayValue(inputValue) {
      if (inputValue > this.maxFinalNetPayValue) {
        this.finalNetPayValue = this.maxFinalNetPayValue;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
ol {
  padding-left: 0.75rem;
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.last-day-pay {
  &__title {
    margin-bottom: 12px;
    font-size: 1.1rem;
  }

  &__form {
    margin-bottom: 26px;

    &-group {
      margin-bottom: 6px;
    }
  }
}

.success-line {
  margin: 0;

  &._heading {
    margin-bottom: 10px;
  }

  &._marked {
    font-weight: 700;
  }
}

.next-steps {
  margin-top: 20px;
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.download-link {
  padding: 0;
  vertical-align: baseline;
}

.summary {
  &__row {
    display: flex;
    justify-content: space-between;
    color: var(--palette-color-brand-primary);
    font-size: 14px;
    font-weight: 400;
    line-height: 22px;

    & + & {
      margin-top: 8px;
    }

    &._total {
      margin-top: 12px;
      padding-top: 10px;
      border-top: 1px solid var(--palette-color-base-background);
      color: var(--palette-color-brand-primary);
      font-weight: 700;
    }
  }

  &__header {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    margin-bottom: 12px;
  }

  &__subtitle {
    margin: 8px 0 0;
    color: var(--palette-color-danger);
    font-size: 14px;
    font-weight: 400;
    line-height: 22px;
    text-align: center;
  }

  &__icon {
    margin-bottom: 5px;
    font-size: 3rem;
    color: var(--palette-color-warning);
  }
}
</style>
